import axios from 'axios';
import { headerConfig } from '../utils';
import { API_URL } from '../../config/environment-variables';
import {
  FETCH_ADMINISTRATION_BOOKKEEPING,
  FETCH_ADMINISTRATION_DATA,
  FETCH_ADMINISTRATION_DICTIONARIES,
  FETCH_ADMINISTRATION_EXTRA_DATA,
  FETCH_ADMINISTRATION_LIST_DATA,
  FETCH_VAT_OPTIONS,
  REQUEST_RESULT,
} from '../types';
import { ITEMS_PER_PAGE } from '../../components/pages/administration/CommonAdministrationPage';
import { ADMINISTRATION_DATA_TYPE } from '../../components/pages/administration/AdministrationItemsFormFields';
import FileSaver from 'file-saver';
import { analyticEvent } from '../../analytics';
import { tableTypeHandlerMapping } from './administration-utils';

const ADMINISTRATION_URL = `${API_URL}/items`;

export function fetchAdministrationExtraData(tableType, machineId, urlParameter = 'extraData', onlyAllowCategories = 'false') {
  return dispatch => {
    axios.get(`${ADMINISTRATION_URL}/${urlParameter}/${tableType}/${machineId}/${onlyAllowCategories}`,
      dispatch(headerConfig()))
      .then((response) => {
        dispatch({
          type: FETCH_ADMINISTRATION_EXTRA_DATA,
          payload: response.data,
        });
      }).catch(error => {
      dispatch({
        type: REQUEST_RESULT,
        payload: { message: error.response.data },
      });
    });
  };
}

export function fetchVATOptions() {
  return dispatch => {
    axios.get(`${API_URL}/items/getVatList`, dispatch(headerConfig()))
      .then((response) => {
        dispatch({
          type: FETCH_VAT_OPTIONS,
          payload: response.data.VatList,
        });
      }).catch(error => error);
  };
}

export function fetchDictionaries(machineId) {
  return dispatch => {
    axios.get(`${API_URL}/items/listData/WordLists/${machineId}`, dispatch(headerConfig()))
      .then((response) => {
        dispatch({
          type: FETCH_ADMINISTRATION_DICTIONARIES,
          payload: response.data.WordLists,
        });
      }).catch(error => {
      dispatch({
        type: REQUEST_RESULT,
        payload: { message: error.response.data },
      });
    });
  };
}

export function fetchAdministrationListData(tableType, machineId, urlParameter = 'ListData') {
  let mergedResponses = [];
  return dispatch => {
    axios.all([...tableType].map(type =>
      axios.get(`${ADMINISTRATION_URL}/${urlParameter}/${type}/${machineId}`, dispatch(headerConfig()))
        .then(response => {
          mergedResponses = { ...mergedResponses, ...response.data };
        }),
    )).then(() => {
      dispatch({
        type: FETCH_ADMINISTRATION_LIST_DATA,
        payload: mergedResponses,
      });
    }).catch(error => {
      dispatch({
        type: REQUEST_RESULT,
        payload: { message: error.response.data },
      });
    });
  };
}

export function checkIfCustomerCodeExists(machineId, values, setSubmitting, setFieldError, setLoadingProgress) {
  const { CustomerCode } = values;
  
  return dispatch => {
    axios.get(`${ADMINISTRATION_URL}/${ADMINISTRATION_DATA_TYPE.CUSTOMER_TABLE}/${machineId}/${ITEMS_PER_PAGE}/0/${CustomerCode}`,
      {
        ...dispatch(headerConfig()),
        onDownloadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setLoadingProgress && setLoadingProgress(progress);
        },
      },
    )
      .then(response => {
          setSubmitting && setSubmitting(false);
          if (!response.data.Items || response.data.Items.length !== 1) {
            setFieldError('CustomerCode', 'Such customer code doesn\'t exist');
          }
        },
      )
      .catch(() => {
          setSubmitting && setSubmitting(false);
        },
      );
  };
  
}

export function fetchAdministrationData(tableType, machineId, values, pageNumber, setSubmitting, setLoadingProgress) {
  const { text } = values;
  let transformedResponse = [];
  return dispatch => {
    axios.get(`${ADMINISTRATION_URL}/${tableType}/${machineId}/${ITEMS_PER_PAGE}/${pageNumber}/${text ? text : null}`,
      // FIXME: API should not require null as parameter
      {
        ...dispatch(headerConfig()),
        onDownloadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setLoadingProgress && setLoadingProgress(progress);
        },
      },
    )
      .then(response => {
          transformedResponse = {
            items: response.data.Items,
            machineId: machineId,
            pageCount: response.data.PageCount,
            totalCount: response.data.TotalItemCount,
            itemsFrom: response.data.ShowingNowFrom,
            itemsTo: response.data.ShowingNowTo,
            searchText: text,
          };
        },
      )
      .then(() => {
        setSubmitting && setSubmitting(false);
        dispatch({
          type: FETCH_ADMINISTRATION_DATA,
          payload: transformedResponse,
        });
      })
      .catch(error => {
          setSubmitting && setSubmitting(false);
          if (error && error.response.status === 400) {
            dispatch({
              type: REQUEST_RESULT,
              payload: { message: error.response.data },
            });
          }
        },
      );
  };
}

export function updateAdministrationItem(tableType, machineId, values, setSubmitting, page, setLoadingProgress, searchText) {
  if (tableType in tableTypeHandlerMapping) {
    tableTypeHandlerMapping[tableType](values);
  }
  
  return dispatch => {
    axios.put(`${ADMINISTRATION_URL}/${tableType}/${machineId}`,
      values,
      dispatch(headerConfig()))
      .then(() => {
        setSubmitting(false);
        dispatch(fetchAdministrationData(tableType, machineId, { text: searchText }, page, setSubmitting, setLoadingProgress));
      })
      .catch(error => {
        setSubmitting && setSubmitting(false);
        if (error) {
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: error.response.data },
          });
        }
      });
  };
}

export function addNewAdministrationItem(tableType, machineId, values, setSubmitting, setNewlyCreatedItemName) {
  if (tableType in tableTypeHandlerMapping) {
    tableTypeHandlerMapping[tableType](values);
  }
  
  return dispatch => {
    axios.post(`${ADMINISTRATION_URL}/${tableType}/${machineId}`,
      values,
      dispatch(headerConfig()))
      .then((response) => {
          setSubmitting(false);
          setNewlyCreatedItemName(response.data?.Name);
          dispatch({
            type: FETCH_ADMINISTRATION_DATA,
            payload: { items: [response.data] },
          });
          if (tableType === ADMINISTRATION_DATA_TYPE.CATEGORY_TABLE) {
            dispatch(fetchAdministrationExtraData(ADMINISTRATION_DATA_TYPE.INVENTORY_TABLE, machineId));
          }
        },
      )
      .catch(error => {
        setSubmitting && setSubmitting(false);
        if (error) {
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: error.response.data },
          });
        }
      });
  };
}

export function removeAdministrationItem(tableType, machineId, itemId, setSubmitting, page, setLoadingProgress, searchText) {
  return dispatch => {
    axios.delete(`${ADMINISTRATION_URL}/${tableType}/${machineId}/${itemId}`,
      dispatch(headerConfig()))
      .then(() => {
        dispatch(fetchAdministrationData(tableType, machineId, { text: searchText }, page, setSubmitting, setLoadingProgress));
      }).catch(error => {
      dispatch({
        type: REQUEST_RESULT,
        payload: { message: error.response.data },
      });
    });
  };
}

export function removeAdministrationCategory(tableType, machineId, itemId, setSubmitting, page, setLoadingProgress, searchText) {
  return dispatch => {
    axios.delete(`${API_URL}/${tableType}/${itemId}`,
      dispatch(headerConfig()))
      .then(() => {
          dispatch(fetchAdministrationData(tableType, machineId, { text: searchText }, page, setSubmitting, setLoadingProgress));
        },
      ).catch(error => {
      if (error) {
        dispatch({
          type: REQUEST_RESULT,
          payload: {
            message: error.response.data === 'Category has InventoryItem items with references to it.'
              ? 'Denna kategori har kopplade artiklar. Ta bort artiklarna tillhörande denna kategori först.'
              : error.response.data,
          },
        });
      }
    });
  };
}

export function fetchAdministrationDocs() {
  return dispatch => {
    axios.get(`${ADMINISTRATION_URL}/documentation`, dispatch(headerConfig()))
      .then(response => {
        console.log(response);
      })
      .catch(error => {
          if (error && error.response.status === 400) {
            dispatch({
              type: REQUEST_RESULT,
              payload: { message: error.response.data },
            });
          }
        },
      );
  };
}

export const exportAdministrationDataToExcel = (type, machineId, setLoadingProgress, formValues) => {
  const { text } = formValues;
  const optionalSearch = text ? `${text}/` : '';
  setLoadingProgress(0);
  return dispatch => {
    axios.get(`${ADMINISTRATION_URL}/${type}/${machineId}/${optionalSearch}excel`,
      {
        ...dispatch(headerConfig()),
        responseType: 'arraybuffer',
        onDownloadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setLoadingProgress && setLoadingProgress(progress);
        },
      }).then(response => {
      const blob = new Blob([response.data], { type: `application/xlsx` });
      const fileName = `${type}_${machineId}.xlsx`;
      FileSaver.saveAs(blob, fileName);
    })
      .catch(error => {
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: `Download error: ${error.response.status}` },
        });
      });
  };
};

export function fetchBookkeepingSettings(machineId, setSubmitting) {
  return dispatch => {
    axios.get(`${API_URL}/bookkeeping/${machineId}`, dispatch(headerConfig()))
      .then(response => {
        dispatch({
          type: FETCH_ADMINISTRATION_BOOKKEEPING,
          payload: response.data,
        });
      })
      .catch(() => {
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: 'Error: fetching bookkeeping data' },
        });
      }).finally(() => {
      setSubmitting && setSubmitting(false);
    });
  };
}

export function saveBookkeepingData(machineId, formValues, setSubmitting, setSaveStatus, setLoading) {
  setLoading(true);
  const bookkeepingData = {
    ForMachineId: machineId,
    ...formValues,
  };
  return dispatch => {
    axios.post(`${API_URL}/bookkeeping`, bookkeepingData, dispatch(headerConfig()))
      .then(() => {
        setSaveStatus(true);
        analyticEvent('Bookkeeping Click', 'Bookkeeping edit', 'Success', { machineId: machineId });
      })
      .catch(() => {
        setSaveStatus(false);
        analyticEvent('Bookkeeping Click', 'Bookkeeping edit', 'Failure', { machineId: machineId });
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: 'Error: saving bookkeeping data' },
        });
      }).finally(() => {
      setLoading && setLoading(false);
      setSubmitting && setSubmitting(false);
    });
  };
}

export function clearAdministrationExtraData() {
  return dispatch => {
    dispatch({
        type: FETCH_ADMINISTRATION_EXTRA_DATA,
        payload: undefined,
      },
      {
        type: FETCH_ADMINISTRATION_LIST_DATA,
        payload: undefined,
      },
      {
        type: FETCH_ADMINISTRATION_DICTIONARIES,
        payload: undefined,
      },
      {
        type: FETCH_ADMINISTRATION_BOOKKEEPING,
        payload: undefined,
      },
      {
        type: FETCH_VAT_OPTIONS,
        payload: undefined,
      },
    );
  };
}

export function clearAdministrationData() {
  return dispatch => {
    dispatch({
        type: FETCH_ADMINISTRATION_DATA,
        payload: undefined,
      },
    );
  };
}
